import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Stack,
  Container,
  Tooltip,
  OutlinedInput,
  Tabs,
  Tab,
  Grid,
  Link,
} from "@mui/material";
import {
  Hub as ConnectIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  Article as ArticleIcon,
  Stars as StarsIcon,
  TrendingUp as WeightsIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Gavel as AppealIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  useGetConnectDashboardQuery,
  useLazyExportConnectLeaderboardXlsxQuery,
  useGetConnectMatchesQuery,
  useGetConnectMatchQuery,
  useGetConnectReportsQuery,
  useHideConnectReportMutation,
  useShowConnectReportMutation,
  useUpdateConnectReportInsightMutation,
  useGetConnectScoresQuery,
  useAppealConnectPenaltyMutation,
  useGetConnectWeightsQuery,
  useCreateConnectWeightMutation,
  useUpdateConnectWeightMutation,
  useDeleteConnectWeightMutation,
  type ConnectMatchListStatus,
  type ConnectReportFeedItem,
  type ConnectScoreItem,
  type ConnectScoreReason,
  type ConnectWeightPeriod,
  type GetConnectMatchesParams,
  type GetConnectReportsParams,
  type GetConnectScoresParams,
} from "../../../entities/admin-connect";
import { useGetMyPermissionsQuery } from "../../../entities/me";
import { ADMIN_PERMISSION_RAPL_CONNECT } from "../../../shared/constants/admin-permission-ids";
import { ResidentCaliber } from "../../../entities/admin-events";
import {
  getCaliberColor,
  renderCaliberName,
} from "../../../shared/lib/caliber";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`connect-tabpanel-${index}`}
      sx={{ pt: 2 }}
    >
      {value === index ? children : null}
    </Box>
  );
}

const MATCH_STATUS_DESCRIPTION: Record<ConnectMatchListStatus, string> = {
  active: "Встреча ещё не состоялась",
  completed: "Встреча проведена, отчёты сданы",
  penalized: "Встреча не состоялась (начислен штраф)",
};

const MATCH_STATUS_CHIP_LABEL: Record<ConnectMatchListStatus, string> = {
  active: "Активный",
  completed: "Завершён",
  penalized: "Не состоялась",
};

function matchStatusChipColor(
  s: ConnectMatchListStatus,
): "default" | "success" | "error" {
  if (s === "completed") return "success";
  if (s === "penalized") return "error";
  return "default";
}

const SCORE_REASON_LABEL: Record<ConnectScoreReason, string> = {
  rating: "Рейтинг",
  penalty: "Штраф",
  penaltyAppeal: "Обжалование",
  weightBonus: "Бонус (коэфф.)",
};

const MEETING_PLACE_LABEL: Record<string, string> = {
  inPerson: "Личная встреча",
  videoCall: "Видеозвонок",
};

function participantName(p: { firstName: string; lastName: string }) {
  return `${p.firstName} ${p.lastName}`.trim() || "—";
}

export const ConnectManagement = () => {
  const [tab, setTab] = useState(0);

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetMyPermissionsQuery();
  const canAccessConnect =
    permissionsData?.permissions?.includes(ADMIN_PERMISSION_RAPL_CONNECT) ??
    false;
  const skipConnect = isLoadingPermissions || !canAccessConnect;

  /* ——— Dashboard ——— */
  const { data: dashboard, isLoading: dashLoading } =
    useGetConnectDashboardQuery(undefined, { skip: skipConnect });
  const [exportXlsx, { isFetching: exportLoading }] =
    useLazyExportConnectLeaderboardXlsxQuery();
  const [leaderboardPeriodDate, setLeaderboardPeriodDate] =
    useState<Date | null>(new Date());

  const handleExportLeaderboard = async () => {
    try {
      const period = leaderboardPeriodDate
        ? format(leaderboardPeriodDate, "yyyy-MM")
        : undefined;

      const blob = await exportXlsx(period ? { period } : undefined).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const suffix = period ?? "current";
      link.download = `connect-leaderboard-${suffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed:", e);
    }
  };

  /* ——— Matches ——— */
  const [mMonth, setMMonth] = useState<number | "">("");
  const [mYear, setMYear] = useState<number | "">("");
  const [mStatus, setMStatus] = useState<ConnectMatchListStatus | "">("");
  const [mSortBy, setMSortBy] = useState<
    "statuschangedat" | "perioddate" | "id"
  >("perioddate");
  const [mSortOrder, setMSortOrder] = useState<"asc" | "desc">("asc");
  const [mPage, setMPage] = useState(0);
  const [mLimit, setMLimit] = useState(25);
  const [mPageTokenHistory, setMPageTokenHistory] = useState<
    (string | undefined)[]
  >([]);
  const [mPageToken, setMPageToken] = useState<string | undefined>(undefined);
  const [matchDetailId, setMatchDetailId] = useState<number | null>(null);

  const goToMatchDetail = (matchId: number) => {
    setTab(1);
    setMatchDetailId(matchId);
  };

  const matchQueryParams: GetConnectMatchesParams = useMemo(
    () => ({
      month: mMonth === "" ? undefined : mMonth,
      year: mYear === "" ? undefined : mYear,
      status: mStatus === "" ? undefined : mStatus,
      sort: mSortBy,
      order: mSortOrder,
      pageToken: mPageToken,
      limit: mLimit,
    }),
    [mMonth, mYear, mStatus, mSortBy, mSortOrder, mPageToken, mLimit],
  );

  const { data: matchesData, isLoading: matchesLoading } =
    useGetConnectMatchesQuery(matchQueryParams, { skip: skipConnect });
  const matchItems = matchesData?.items ?? [];
  const matchNext = matchesData?.pageToken?.trim() || undefined;

  const { data: matchDetailData, isFetching: matchDetailLoading } =
    useGetConnectMatchQuery(matchDetailId!, {
      skip: skipConnect || matchDetailId == null,
    });

  const resetMatchPagination = useCallback(() => {
    setMPage(0);
    setMPageTokenHistory([]);
    setMPageToken(undefined);
  }, []);

  const goToMeetingsCurrentMonth = useCallback(() => {
    const now = new Date();
    setMMonth(now.getMonth() + 1);
    setMYear(now.getFullYear());
    setMStatus("");
    resetMatchPagination();
    setTab(1);
  }, [resetMatchPagination]);

  const handleMatchSort = (field: "statuschangedat" | "perioddate" | "id") => {
    if (mSortBy === field) {
      setMSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setMSortBy(field);
      setMSortOrder("asc");
    }
    resetMatchPagination();
  };

  const handleMatchPage = (_: unknown, newPage: number) => {
    if (newPage > mPage && matchNext) {
      setMPageTokenHistory((h) => [...h, mPageToken]);
      setMPageToken(matchNext);
      setMPage(newPage);
    } else if (newPage < mPage) {
      const h = [...mPageTokenHistory];
      const prev = h.pop();
      setMPageTokenHistory(h);
      setMPageToken(prev);
      setMPage(newPage);
    }
  };

  /* ——— Reports ——— */
  const [rHidden, setRHidden] = useState<"" | "all" | "true" | "false">("all");
  const [rSearch, setRSearch] = useState("");
  const [rSortBy, setRSortBy] = useState<"createdat" | "id">("createdat");
  const [rSortOrder, setRSortOrder] = useState<"asc" | "desc">("desc");
  const [rPage, setRPage] = useState(0);
  const [rLimit, setRLimit] = useState(25);
  const [rPageTokenHistory, setRPageTokenHistory] = useState<
    (string | undefined)[]
  >([]);
  const [rPageToken, setRPageToken] = useState<string | undefined>(undefined);

  const reportParams: GetConnectReportsParams = useMemo(() => {
    let isHidden: boolean | undefined;
    if (rHidden === "true") isHidden = true;
    else if (rHidden === "false") isHidden = false;
    return {
      isHidden,
      search: rSearch.trim() || undefined,
      sort: rSortBy,
      order: rSortOrder,
      pageToken: rPageToken,
      limit: rLimit,
    };
  }, [rHidden, rSearch, rSortBy, rSortOrder, rPageToken, rLimit]);

  const { data: reportsData, isLoading: reportsLoading } =
    useGetConnectReportsQuery(reportParams, { skip: skipConnect });
  const [hideReport] = useHideConnectReportMutation();
  const [showReport] = useShowConnectReportMutation();
  const [updateInsight, { isLoading: updatingInsight }] =
    useUpdateConnectReportInsightMutation();

  const [insightDialog, setInsightDialog] =
    useState<ConnectReportFeedItem | null>(null);
  const [insightText, setInsightText] = useState("");

  const reportItems = reportsData?.items ?? [];
  const reportNext = reportsData?.pageToken?.trim() || undefined;

  const resetReportPagination = useCallback(() => {
    setRPage(0);
    setRPageTokenHistory([]);
    setRPageToken(undefined);
  }, []);

  const handleReportSort = (field: "createdat" | "id") => {
    if (rSortBy === field) {
      setRSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setRSortBy(field);
      setRSortOrder("asc");
    }
    resetReportPagination();
  };

  const handleReportPage = (_: unknown, newPage: number) => {
    if (newPage > rPage && reportNext) {
      setRPageTokenHistory((h) => [...h, rPageToken]);
      setRPageToken(reportNext);
      setRPage(newPage);
    } else if (newPage < rPage) {
      const h = [...rPageTokenHistory];
      const prev = h.pop();
      setRPageTokenHistory(h);
      setRPageToken(prev);
      setRPage(newPage);
    }
  };

  const openEditInsight = (row: ConnectReportFeedItem) => {
    setInsightDialog(row);
    setInsightText(row.insight);
  };

  const saveInsight = async () => {
    if (!insightDialog) return;
    try {
      await updateInsight({
        id: insightDialog.id,
        insight: insightText,
      }).unwrap();
      setInsightDialog(null);
    } catch (e) {
      console.error(e);
    }
  };

  /* ——— Scores ——— */
  const [sMonth, setSMonth] = useState<number | "">("");
  const [sYear, setSYear] = useState<number | "">("");
  const [sReason, setSReason] = useState<ConnectScoreReason | "">("");
  const [sUserId, setSUserId] = useState("");
  const [sSortBy, setSSortBy] = useState<"perioddate" | "createdat" | "id">(
    "id",
  );
  const [sSortOrder, setSSortOrder] = useState<"asc" | "desc">("asc");
  const [sPage, setSPage] = useState(0);
  const [sLimit, setSLimit] = useState(25);
  const [sPageTokenHistory, setSPageTokenHistory] = useState<
    (string | undefined)[]
  >([]);
  const [sPageToken, setSPageToken] = useState<string | undefined>(undefined);

  const scoreParams: GetConnectScoresParams = useMemo(
    () => ({
      month: sMonth === "" ? undefined : sMonth,
      year: sYear === "" ? undefined : sYear,
      reason: sReason === "" ? undefined : sReason,
      userId: sUserId.trim() || undefined,
      sort: sSortBy,
      order: sSortOrder,
      pageToken: sPageToken,
      limit: sLimit,
    }),
    [sMonth, sYear, sReason, sUserId, sSortBy, sSortOrder, sPageToken, sLimit],
  );

  const { data: scoresData, isLoading: scoresLoading } =
    useGetConnectScoresQuery(scoreParams, { skip: skipConnect });
  const [appealPenalty, { isLoading: appealing }] =
    useAppealConnectPenaltyMutation();

  const [appealRow, setAppealRow] = useState<ConnectScoreItem | null>(null);
  const [appealPoints, setAppealPoints] = useState(5);
  const [appealNote, setAppealNote] = useState("");

  const scoreItems = scoresData?.items ?? [];
  const scoreNext = scoresData?.pageToken?.trim() || undefined;

  const resetScorePagination = useCallback(() => {
    setSPage(0);
    setSPageTokenHistory([]);
    setSPageToken(undefined);
  }, []);

  const handleScoreSort = (field: "perioddate" | "createdat" | "id") => {
    if (sSortBy === field) {
      setSSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSSortBy(field);
      setSSortOrder("asc");
    }
    resetScorePagination();
  };

  const handleScorePage = (_: unknown, newPage: number) => {
    if (newPage > sPage && scoreNext) {
      setSPageTokenHistory((h) => [...h, sPageToken]);
      setSPageToken(scoreNext);
      setSPage(newPage);
    } else if (newPage < sPage) {
      const h = [...sPageTokenHistory];
      const prev = h.pop();
      setSPageTokenHistory(h);
      setSPageToken(prev);
      setSPage(newPage);
    }
  };

  const submitAppeal = async () => {
    if (!appealRow) return;
    try {
      const note = appealNote.trim();
      if (!note) {
        return;
      }
      await appealPenalty({
        logId: appealRow.id,
        body: { points: appealPoints, appealNote: note },
      }).unwrap();
      setAppealRow(null);
    } catch (e) {
      console.error(e);
    }
  };

  /* ——— Weights ——— */
  const { data: weightsData, isLoading: weightsLoading } =
    useGetConnectWeightsQuery(undefined, { skip: skipConnect });
  const [createWeight, { isLoading: creatingW }] =
    useCreateConnectWeightMutation();
  const [updateWeight, { isLoading: updatingW }] =
    useUpdateConnectWeightMutation();
  const [deleteWeight, { isLoading: deletingW }] =
    useDeleteConnectWeightMutation();

  const [weightDialog, setWeightDialog] = useState<"create" | "edit" | null>(
    null,
  );
  const [editingWeight, setEditingWeight] =
    useState<ConnectWeightPeriod | null>(null);
  const [wMultiplier, setWMultiplier] = useState(100);
  const [wBegin, setWBegin] = useState("");
  const [wEnd, setWEnd] = useState("");
  const [wNote, setWNote] = useState("");

  const openCreateWeight = () => {
    setEditingWeight(null);
    setWMultiplier(100);
    setWBegin("");
    setWEnd("");
    setWNote("");
    setWeightDialog("create");
  };

  const openEditWeight = (w: ConnectWeightPeriod) => {
    setEditingWeight(w);
    setWMultiplier(w.multiplier);
    try {
      setWBegin(format(parseISO(w.beginAt), "yyyy-MM-dd'T'HH:mm"));
      setWEnd(format(parseISO(w.endAt), "yyyy-MM-dd'T'HH:mm"));
    } catch {
      setWBegin(w.beginAt.slice(0, 16));
      setWEnd(w.endAt.slice(0, 16));
    }
    setWNote(w.note);
    setWeightDialog("edit");
  };

  const submitWeight = async () => {
    const body = {
      multiplier: wMultiplier,
      beginAt: new Date(wBegin).toISOString(),
      endAt: new Date(wEnd).toISOString(),
      note: wNote.trim(),
    };
    try {
      if (weightDialog === "create") {
        await createWeight(body).unwrap();
      } else if (weightDialog === "edit" && editingWeight) {
        await updateWeight({ id: editingWeight.id, body }).unwrap();
      }
      setWeightDialog(null);
    } catch (e) {
      console.error(e);
    }
  };

  const [deleteConfirm, setDeleteConfirm] =
    useState<ConnectWeightPeriod | null>(null);

  if (isLoadingPermissions) {
    return (
      <Container sx={{ py: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!canAccessConnect) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography color="text.secondary">
          Нет доступа к разделу «RAPL Connect».
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ConnectIcon fontSize="large" color="primary" />
        <Typography fontWeight={600} fontSize={24}>
          RAPL Connect
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab icon={<DashboardIcon />} iconPosition="start" label="Дашборд" />
        <Tab icon={<EventIcon />} iconPosition="start" label="Встречи" />
        <Tab icon={<ArticleIcon />} iconPosition="start" label="Отчёты" />
        <Tab icon={<StarsIcon />} iconPosition="start" label="Баллы" />
        <Tab icon={<WeightsIcon />} iconPosition="start" label="Коэффициенты" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        {dashLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">
                      Всего встреч (месяц)
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      onClick={goToMeetingsCurrentMonth}
                      underline="hover"
                      sx={{
                        typography: "h4",
                        color: "primary.main",
                        border: 0,
                        background: "none",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {dashboard?.totalMatches ?? 0}
                    </Link>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">
                      Завершённых
                    </Typography>
                    <Typography variant="h4">
                      {dashboard?.completedMatches ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">
                      Завершённых встреч, %
                    </Typography>
                    <Typography variant="h4">
                      {dashboard?.showRatePercent != null
                        ? `${dashboard.showRatePercent.toFixed(1)}%`
                        : "—"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Card>
              <CardContent>
                <Typography fontWeight={600} gutterBottom>
                  Экспорт рейтинга (Excel)
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ru}
                  >
                    <DatePicker
                      label="Период"
                      views={["year", "month"]}
                      openTo="month"
                      value={leaderboardPeriodDate}
                      onChange={(date) => setLeaderboardPeriodDate(date)}
                      slotProps={{
                        textField: { size: "small", sx: { minWidth: 220 } },
                      }}
                    />
                  </LocalizationProvider>
                  <Button
                    variant="contained"
                    startIcon={
                      exportLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={handleExportLeaderboard}
                    disabled={exportLoading}
                  >
                    Скачать XLSX
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <ArrowUpIcon
                fontSize="inherit"
                sx={{ verticalAlign: "middle" }}
              />
              <ArrowDownIcon
                fontSize="inherit"
                sx={{ verticalAlign: "middle" }}
              />{" "}
              Сортировка по заголовкам колонок
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Месяц (1–12)"
                  type="number"
                  size="small"
                  fullWidth
                  value={mMonth}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMMonth(v === "" ? "" : Number(v));
                    resetMatchPagination();
                  }}
                  inputProps={{ min: 1, max: 12 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Год"
                  type="number"
                  size="small"
                  fullWidth
                  value={mYear}
                  onChange={(e) => {
                    const v = e.target.value;
                    setMYear(v === "" ? "" : Number(v));
                    resetMatchPagination();
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={mStatus}
                    label="Статус"
                    onChange={(e) => {
                      setMStatus(e.target.value as ConnectMatchListStatus | "");
                      resetMatchPagination();
                    }}
                    input={<OutlinedInput label="Статус" />}
                  >
                    <MenuItem value="">Все</MenuItem>
                    <MenuItem value="active">Активна</MenuItem>
                    <MenuItem value="completed">Завершена</MenuItem>
                    <MenuItem value="penalized">Не состоялась</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleMatchSort("id")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    ID
                    {mSortBy === "id" &&
                      (mSortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleMatchSort("perioddate")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Период
                    {mSortBy === "perioddate" &&
                      (mSortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Участники</TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleMatchSort("statuschangedat")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Статус изменён
                    {mSortBy === "statuschangedat" &&
                      (mSortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matchesLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : matchItems.length > 0 ? (
                matchItems.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => setMatchDetailId(row.id)}
                  >
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.periodDate}</TableCell>
                    <TableCell>
                      <Tooltip title={MATCH_STATUS_DESCRIPTION[row.status]}>
                        <Chip
                          label={MATCH_STATUS_CHIP_LABEL[row.status]}
                          size="small"
                          color={matchStatusChipColor(row.status)}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {row.participants.map((p) => (
                          <Tooltip key={p.userId} title={participantName(p)}>
                            <Avatar
                              src={p.avatarUrl}
                              sx={{ width: 32, height: 32 }}
                            >
                              {p.firstName?.[0]}
                            </Avatar>
                          </Tooltip>
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {format(
                        parseISO(row.statusChangedAt),
                        "dd.MM.yyyy HH:mm",
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={-1}
            rowsPerPage={mLimit}
            page={mPage}
            onPageChange={handleMatchPage}
            onRowsPerPageChange={(e) => {
              setMLimit(parseInt(e.target.value, 10));
              resetMatchPagination();
            }}
            labelDisplayedRows={() => ""}
          />
        </TableContainer>

        <Dialog
          open={matchDetailId != null}
          onClose={() => setMatchDetailId(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Встреча #{matchDetailId}</DialogTitle>
          <DialogContent dividers>
            {matchDetailLoading ? (
              <CircularProgress />
            ) : matchDetailData?.match ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Период: {matchDetailData.match.periodDate}
                </Typography>
                <Typography variant="body2">
                  Статус:{" "}
                  {MATCH_STATUS_DESCRIPTION[matchDetailData.match.matchStatus]}
                </Typography>
                <Box sx={{ backgroundColor: "#414141", p: 2, borderRadius: 1 }}>
                  <Typography fontWeight={600} mb={2}>
                    Участники
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {matchDetailData.match.participants.map((p) => (
                      <Stack
                        key={p.userId}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Avatar src={p.avatarUrl}>{p.firstName?.[0]}</Avatar>
                        <Box>
                          <Typography variant="body2">
                            {participantName(p)}
                          </Typography>
                          <Chip
                            label={renderCaliberName(
                              p.caliber as ResidentCaliber,
                            )}
                            color={getCaliberColor(
                              p.caliber as ResidentCaliber,
                            )}
                            size="small"
                          />
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
                <Box sx={{ backgroundColor: "#414141", p: 2, borderRadius: 1 }}>
                  <Typography fontWeight={600} mb={2}>
                    Отчёты
                  </Typography>
                  {(matchDetailData.match.reports ?? []).length === 0 ? (
                    <Typography color="text.secondary">Нет отчётов</Typography>
                  ) : (
                    (matchDetailData.match.reports ?? []).map((rep) => {
                      const author =
                        matchDetailData.match.participants.find(
                          (p) => p.userId === rep.authorUserId,
                        ) ?? null;

                      return (
                        <Paper
                          key={rep.id}
                          variant="outlined"
                          sx={{ p: 2, backgroundColor: "#333333", mb: 2 }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Avatar src={author?.avatarUrl}>
                                {author?.firstName?.[0]}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Автор отчёта
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {author
                                    ? participantName(author)
                                    : "Неизвестный участник"}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip
                                label={
                                  MEETING_PLACE_LABEL[rep.meetingPlace] ??
                                  rep.meetingPlace
                                }
                                size="small"
                              />
                              <Chip
                                label={`Рейтинг: ${rep.rating}`}
                                size="small"
                              />
                              {rep.isHidden && (
                                <Chip
                                  label="Скрыт"
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </Stack>
                            {rep.photoUrl ? (
                              <Box
                                component="img"
                                src={rep.photoUrl}
                                alt=""
                                sx={{ maxWidth: 200, borderRadius: 1 }}
                              />
                            ) : null}
                            <Typography variant="subtitle2">Инсайт</Typography>
                            <Typography variant="body2" pb={1}>
                              {rep.insight || "—"}
                            </Typography>
                            <Typography variant="subtitle2">
                              О партнёре
                            </Typography>
                            <Typography variant="body2">
                              {rep.aboutPartner || "—"}
                            </Typography>
                          </Stack>
                        </Paper>
                      );
                    })
                  )}
                </Box>
              </Stack>
            ) : (
              <Typography>Не удалось загрузить</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMatchDetailId(null)}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Поиск по инсайту"
                  value={rSearch}
                  onChange={(e) => {
                    setRSearch(e.target.value);
                    resetReportPagination();
                  }}
                  InputProps={{ startAdornment: <SearchIcon /> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Видимость</InputLabel>
                  <Select
                    value={rHidden}
                    label="Видимость"
                    onChange={(e) => {
                      setRHidden(e.target.value as typeof rHidden);
                      resetReportPagination();
                    }}
                    input={<OutlinedInput label="Видимость" />}
                  >
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value="false">Только видимые</MenuItem>
                    <MenuItem value="true">Только скрытые</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Встреча</TableCell>
                <TableCell>Период</TableCell>
                <TableCell>Автор</TableCell>
                <TableCell>Партнёр</TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleReportSort("createdat")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Создан
                    {rSortBy === "createdat" &&
                      (rSortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell>Инсайт</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : reportItems.length > 0 ? (
                reportItems.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => goToMatchDetail(row.matchId)}
                        sx={{ cursor: "pointer" }}
                      >
                        #{row.matchId}
                      </Link>
                    </TableCell>
                    <TableCell>{row.periodDate}</TableCell>
                    <TableCell>{participantName(row.author)}</TableCell>
                    <TableCell>{participantName(row.partner)}</TableCell>
                    <TableCell>
                      {format(parseISO(row.createdAt), "dd.MM.yyyy HH:mm")}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography noWrap variant="body2">
                        {row.insight}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Редактировать инсайт">
                          <IconButton
                            color="primary"
                            onClick={() => openEditInsight(row)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {row.isHidden ? (
                          <Tooltip title="Показать">
                            <IconButton
                              color="default"
                              onClick={() => showReport(row.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Скрыть">
                            <IconButton
                              color="default"
                              onClick={() => hideReport(row.id)}
                            >
                              <VisibilityOffIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={-1}
            rowsPerPage={rLimit}
            page={rPage}
            onPageChange={handleReportPage}
            onRowsPerPageChange={(e) => {
              setRLimit(parseInt(e.target.value, 10));
              resetReportPagination();
            }}
            labelDisplayedRows={() => ""}
          />
        </TableContainer>

        <Dialog
          open={insightDialog != null}
          onClose={() => setInsightDialog(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Инсайт</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={insightText}
              onChange={(e) => setInsightText(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInsightDialog(null)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={saveInsight}
              disabled={updatingInsight}
            >
              {updatingInsight ? <CircularProgress size={24} /> : "Сохранить"}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <Button
            size="medium"
            variant="outlined"
            startIcon={
              exportLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleExportLeaderboard}
            disabled={exportLoading}
          >
            Экспорт рейтинга (Excel)
          </Button>
        </Box>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Месяц"
                  type="number"
                  size="small"
                  fullWidth
                  value={sMonth}
                  onChange={(e) => {
                    setSMonth(
                      e.target.value === "" ? "" : Number(e.target.value),
                    );
                    resetScorePagination();
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Год"
                  type="number"
                  size="small"
                  fullWidth
                  value={sYear}
                  onChange={(e) => {
                    setSYear(
                      e.target.value === "" ? "" : Number(e.target.value),
                    );
                    resetScorePagination();
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Причина</InputLabel>
                  <Select
                    value={sReason}
                    label="Причина"
                    onChange={(e) => {
                      setSReason(e.target.value as ConnectScoreReason | "");
                      resetScorePagination();
                    }}
                    input={<OutlinedInput label="Причина" />}
                  >
                    <MenuItem value="">Все</MenuItem>
                    <MenuItem value="rating">Рейтинг</MenuItem>
                    <MenuItem value="penalty">Штраф</MenuItem>
                    <MenuItem value="penaltyAppeal">Обжалование</MenuItem>
                    <MenuItem value="weightBonus">Бонус</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="User ID (UUID)"
                  size="small"
                  fullWidth
                  value={sUserId}
                  onChange={(e) => {
                    setSUserId(e.target.value);
                    resetScorePagination();
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Пользователь</TableCell>
                <TableCell>Баллы</TableCell>
                <TableCell>Причина</TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleScoreSort("perioddate")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Период
                    {sSortBy === "perioddate" &&
                      (sSortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell>Встреча</TableCell>
                <TableCell
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleScoreSort("createdat")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Создано
                    {sSortBy === "createdat" &&
                      (sSortOrder === "asc" ? (
                        <ArrowUpIcon fontSize="small" />
                      ) : (
                        <ArrowDownIcon fontSize="small" />
                      ))}
                  </Box>
                </TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scoresLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : scoreItems.length > 0 ? (
                scoreItems.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={row.user.avatarUrl}>
                          {row.user.firstName?.[0]}
                        </Avatar>
                        <Typography variant="body2">
                          {participantName(row.user)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.points}</TableCell>
                    <TableCell>{SCORE_REASON_LABEL[row.reason]}</TableCell>
                    <TableCell>{row.periodDate}</TableCell>
                    <TableCell>
                      {row.matchId != null ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => goToMatchDetail(row.matchId)}
                          sx={{ cursor: "pointer" }}
                        >
                          #{row.matchId}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {format(parseISO(row.createdAt), "dd.MM.yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {row.reason === "penalty" ? (
                          <Tooltip title="Отменить штраф (обжалование)">
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => {
                                setAppealRow(row);
                                setAppealPoints(
                                  Math.max(0, Math.abs(row.points)),
                                );
                                setAppealNote("");
                              }}
                            >
                              <AppealIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {row.hasConsecutivePenalties ? (
                          <Chip
                            label="Серия штрафов"
                            size="small"
                            color="warning"
                          />
                        ) : null}
                        {row.reason !== "penalty" &&
                        !row.hasConsecutivePenalties ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={-1}
            rowsPerPage={sLimit}
            page={sPage}
            onPageChange={handleScorePage}
            onRowsPerPageChange={(e) => {
              setSLimit(parseInt(e.target.value, 10));
              resetScorePagination();
            }}
            labelDisplayedRows={() => ""}
          />
        </TableContainer>

        <Dialog open={appealRow != null} onClose={() => setAppealRow(null)}>
          <DialogTitle>Отмена штрафа</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1, minWidth: 320 }}>
              {appealRow ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Участник:</strong> {participantName(appealRow.user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Период встречи:</strong> {appealRow.periodDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Штраф (баллы):</strong> {appealRow.points}
                  </Typography>
                </>
              ) : null}
              <TextField
                label="Баллы (будет добавлена компенсирующая запись с указанной причиной)"
                type="number"
                value={appealPoints}
                onChange={(e) => setAppealPoints(Number(e.target.value))}
              />
              <TextField
                label="Причина / комментарий к отмене"
                multiline
                minRows={2}
                value={appealNote}
                onChange={(e) => setAppealNote(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAppealRow(null)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={submitAppeal}
              disabled={appealing || !appealNote.trim()}
            >
              {appealing ? <CircularProgress size={24} /> : "Применить"}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tab} index={4}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateWeight}
          >
            Новый период
          </Button>
        </Box>
        {weightsLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Множитель</TableCell>
                  <TableCell>Начало</TableCell>
                  <TableCell>Конец</TableCell>
                  <TableCell>Примечание</TableCell>
                  <TableCell>Активен</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(weightsData?.items ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Нет периодов
                    </TableCell>
                  </TableRow>
                ) : (
                  (weightsData?.items ?? []).map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{w.id}</TableCell>
                      <TableCell>{w.multiplier}</TableCell>
                      <TableCell>
                        {format(parseISO(w.beginAt), "dd.MM.yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        {format(parseISO(w.endAt), "dd.MM.yyyy HH:mm")}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>{w.note}</TableCell>
                      <TableCell>
                        <Chip
                          label={w.isActive ? "Да" : "Нет"}
                          color={w.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => openEditWeight(w)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => setDeleteConfirm(w)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={weightDialog != null}
          onClose={() => setWeightDialog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {weightDialog === "create"
              ? "Период коэффициента"
              : "Редактирование периода"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Множитель"
                type="number"
                value={wMultiplier}
                onChange={(e) => setWMultiplier(Number(e.target.value))}
              />
              <TextField
                label="Начало"
                type="datetime-local"
                value={wBegin}
                onChange={(e) => setWBegin(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Конец"
                type="datetime-local"
                value={wEnd}
                onChange={(e) => setWEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Примечание"
                value={wNote}
                onChange={(e) => setWNote(e.target.value)}
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWeightDialog(null)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={submitWeight}
              disabled={creatingW || updatingW || !wBegin || !wEnd}
            >
              {creatingW || updatingW ? (
                <CircularProgress size={24} />
              ) : (
                "Сохранить"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteConfirm != null}
          onClose={() => setDeleteConfirm(null)}
        >
          <DialogTitle>Удалить период?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>Отмена</Button>
            <Button
              color="error"
              disabled={deletingW}
              onClick={async () => {
                if (deleteConfirm) {
                  try {
                    await deleteWeight(deleteConfirm.id).unwrap();
                    setDeleteConfirm(null);
                  } catch (e) {
                    console.error(e);
                  }
                }
              }}
            >
              {deletingW ? <CircularProgress size={24} /> : "Удалить"}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>
    </Container>
  );
};
